import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import UpgradePlanModal from '../../components/billing/UpgradePlanModal';
import { apiClient, getBillingPortalUrl } from '../../lib/api';
import { useAuthStore } from '../../lib/auth';
import { useWalletBalance } from '../../lib/useWalletBalance';

// Type definitions matching backend schema
interface Subscription {
  id: string;
  workspaceId: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete';
  monthlyTokens: number;
  priceMonthly: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LedgerEntry {
  id: string;
  walletId: string;
  deltaTokens: number;
  balanceAfter: number;
  reason: string;
  metadata: any;
  createdAt: string;
}

interface Invoice {
  id: string;
  stripeInvoiceId: string;
  invoiceNumber: string | null;
  invoiceUrl: string;
  invoicePdf: string | null;
  amountPaid: number;
  status: 'paid' | 'open' | 'void' | 'uncollectible';
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  createdAt: string;
}

// Token pack configurations
const TOKEN_PACKS = [
  { size: 'small', tokens: 1000, price: 990, label: '€9.90', description: '1,000 tokens' },
  { size: 'medium', tokens: 5000, price: 3900, label: '€39.00', description: '5,000 tokens' },
  { size: 'large', tokens: 10000, price: 6900, label: '€69.00', description: '10,000 tokens' },
] as const;

export default function BillingSettingsPage() {
  const { t } = useTranslation();
  const { workspace } = useAuthStore();
  const [searchParams] = useSearchParams();

  // Use wallet balance hook with polling enabled (every 60 seconds)
  const { balance: liveBalance, refresh: refreshBalance } = useWalletBalance({
    enablePolling: true,
    pollingInterval: 60000, // 1 minute
    fetchOnMount: true,
  });

  // State
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [transactions, setTransactions] = useState<LedgerEntry[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugTokenAmount, setDebugTokenAmount] = useState('10000');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Pagination
  const [transactionLimit] = useState(100);
  const [transactionOffset, setTransactionOffset] = useState(0);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(false);

  // Expandable transaction rows
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set());
  const [collapsedJobGroups, setCollapsedJobGroups] = useState<Set<string>>(new Set());

  // Fetch all billing data on mount
  useEffect(() => {
    fetchBillingData();

    // Check for success/cancel URL params from Stripe redirect
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');

    if (success === 'true') {
      // Detect if it's a subscription upgrade or token purchase based on session_id prefix
      const isSubscription = sessionId?.startsWith('cs_test_') || sessionId?.startsWith('cs_live_');

      if (isSubscription) {
        setSuccessMessage(t('billing.messages.subscriptionSuccess', 'Subscription successfully upgraded! 🎉'));
      } else {
        setSuccessMessage(t('billing.messages.purchaseSuccess', 'Tokens successfully purchased!'));
      }

      // Refresh balance and billing data after successful purchase/upgrade
      refreshBalance();
      fetchBillingData();

      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (canceled === 'true') {
      setError(t('billing.messages.purchaseCanceled', 'Purchase was canceled'));
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams, t, refreshBalance]);

  const fetchBillingData = async () => {
    if (!workspace) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Fetch all data in parallel with individual error handling
      // Note: Wallet balance is managed by useWalletBalance hook with polling
      const [subscriptionRes, ledgerRes, invoicesRes] = await Promise.all([
        apiClient.get('/billing/subscription').catch((err) => {
          console.log('Subscription not found (expected for new users):', err.response?.status);
          return { data: { data: { subscription: null } } };
        }),
        apiClient.get(`/billing/ledger?limit=${transactionLimit}&offset=${transactionOffset}`).catch((err) => {
          console.error('Failed to fetch ledger:', err);
          return { data: { data: [] } };
        }),
        apiClient.get('/billing/invoices').catch((err) => {
          console.error('Failed to fetch invoices:', err);
          return { data: { data: [] } };
        }),
      ]);

      // Note: Wallet balance is now managed by useWalletBalance hook with polling

      // Safely extract subscription data - API returns { success, data: {...subscription fields...} }
      const subscriptionData = subscriptionRes.data?.data || null;
      setSubscription(subscriptionData);

      // Safely extract ledger entries - API returns { success, data: [...transactions...], pagination: {...} }
      const ledgerData = ledgerRes.data?.data || [];
      setTransactions(Array.isArray(ledgerData) ? ledgerData : []);
      setHasMoreTransactions(Array.isArray(ledgerData) && ledgerData.length === transactionLimit);

      // Safely extract invoices - API returns { success, data: [...] }
      const invoicesData = invoicesRes.data?.data || [];
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } catch (err: any) {
      console.error('Failed to fetch billing data:', err);
      setError(err.response?.data?.message || t('billing.messages.loadError', 'Failed to load billing data'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchaseTokens = async (packSize: 'small' | 'medium' | 'large') => {
    try {
      setError('');
      const response = await apiClient.post('/billing/tokens/purchase', { packSize });

      // API returns { success, data: { checkoutUrl } }
      const checkoutUrl = response.data?.data?.checkoutUrl || response.data?.checkoutUrl;
      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        setError(t('billing.messages.noCheckoutUrl', 'No checkout URL received'));
      }
    } catch (err: any) {
      console.error('Failed to create checkout session:', err);
      setError(err.response?.data?.message || t('billing.messages.checkoutError', 'Failed to create checkout session'));
    }
  };

  const loadMoreTransactions = async () => {
    try {
      const newOffset = transactionOffset + transactionLimit;
      const response = await apiClient.get(`/billing/ledger?limit=${transactionLimit}&offset=${newOffset}`).catch((err) => {
        console.error('Failed to load more transactions:', err);
        return { data: { data: [] } };
      });

      const newEntries = response.data?.data || [];
      const validEntries = Array.isArray(newEntries) ? newEntries : [];

      setTransactions(prev => [...prev, ...validEntries]);
      setTransactionOffset(newOffset);
      setHasMoreTransactions(validEntries.length === transactionLimit);
    } catch (err: any) {
      console.error('Failed to load more transactions:', err);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/billing/export', {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ledger-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.error('Failed to export CSV:', err);
      setError(t('billing.messages.exportError', 'Failed to export transactions'));
    }
  };

  const handleDebugAddTokens = async () => {
    try {
      setError('');
      const amount = parseInt(debugTokenAmount, 10);

      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid number of tokens');
        return;
      }

      await apiClient.post('/billing/tokens/add-for-testing', { amount });

      setSuccessMessage(`Successfully added ${amount.toLocaleString()} tokens for testing!`);
      setShowDebugModal(false);
      setDebugTokenAmount('10000');

      // Refresh wallet data
      fetchBillingData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to add tokens:', err);
      setError(err.response?.data?.message || 'Failed to add tokens');
    }
  };

  // Handle manage subscription (open Stripe Customer Portal)
  const handleManageSubscription = async () => {
    try {
      setError('');
      const { url } = await getBillingPortalUrl();
      window.open(url, '_blank');
    } catch (err: any) {
      console.error('Failed to open billing portal:', err);
      setError(err.response?.data?.message || 'Failed to open billing portal');
    }
  };

  // Helper functions
  const getPlanLabel = (plan: string) => {
    const labels: Record<string, string> = {
      free: t('billing.subscription.plans.free', 'Free'),
      starter: t('billing.subscription.plans.starter', 'Starter'),
      professional: t('billing.subscription.plans.professional', 'Professional'),
      enterprise: t('billing.subscription.plans.enterprise', 'Enterprise'),
    };
    return labels[plan] || plan;
  };

  const getPlanColor = (plan: string) => {
    const colors: Record<string, string> = {
      free: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700',
      starter: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-300 dark:border-blue-700',
      professional: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-purple-300 dark:border-purple-700',
      enterprise: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-700',
    };
    return colors[plan] || colors.free;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: t('billing.subscription.statuses.active', 'Active'),
      canceled: t('billing.subscription.statuses.canceled', 'Canceled'),
      past_due: t('billing.subscription.statuses.pastDue', 'Past Due'),
      unpaid: t('billing.subscription.statuses.unpaid', 'Unpaid'),
      incomplete: t('billing.subscription.statuses.incomplete', 'Incomplete'),
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'text-green-600 dark:text-green-400',
      canceled: 'text-red-600 dark:text-red-400',
      past_due: 'text-orange-600 dark:text-orange-400',
      unpaid: 'text-red-600 dark:text-red-400',
      incomplete: 'text-gray-600 dark:text-gray-400',
    };
    return colors[status] || colors.incomplete;
  };

  const formatCurrency = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatTokens = (tokens: number) => {
    return `${tokens >= 0 ? '+' : ''}${tokens.toLocaleString()} tokens`;
  };

  // Calculate USD cost from tokens (1 token = $0.0001 USD)
  const calculateUsdCost = (tokens: number) => {
    const TOKEN_TO_USD_RATE = 0.0001;
    return Math.abs(tokens) * TOKEN_TO_USD_RATE;
  };

  // Format USD cost with EUR conversion
  const formatUsdCost = (tokens: number) => {
    const usd = calculateUsdCost(tokens);
    const eur = usd * 0.92; // EUR conversion rate
    return `$${usd.toFixed(5)} USD (€${eur.toFixed(5)})`;
  };

  const getTransactionReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      // Credits
      subscription_renewal: t('billing.reasons.subscriptionRenewal', 'Subscription Renewal'),
      token_pack_purchase: t('billing.reasons.tokenPackPurchase', 'Token Pack Purchase'),
      refund: t('billing.reasons.refund', 'Refund'),
      promo: t('billing.reasons.promo', 'Promotional Credit'),
      migration: t('billing.reasons.migration', 'Data Migration'),

      // Debits - Content Creation
      content_generation: t('billing.reasons.contentGeneration', 'Content Generation'),
      video_processing: t('billing.reasons.videoProcessing', 'Video Processing'),

      // Debits - Brand & Taxonomy
      brand_extraction: t('billing.reasons.brandExtraction', 'Brand Extraction'),
      taxonomy_generation: t('billing.reasons.taxonomyGeneration', 'Taxonomy Generation'),
      taxonomy_keywords: t('billing.reasons.taxonomyKeywords', 'AI Keyword Suggestions'),
      taxonomy_embedding: t('billing.reasons.taxonomyEmbedding', 'Vector Embeddings'),

      // Debits - Content Discovery & Optimization
      content_scraping: t('billing.reasons.contentScraping', 'Content Discovery'),
      blog_optimization: t('billing.reasons.blogOptimization', 'Blog Optimization'),
      ecas_brief_generation: t('billing.reasons.ecasBriefGeneration', 'Content Brief (ECAS)'),

      // Other
      auto_topup: t('billing.reasons.autoTopup', 'Auto Top-up'),
      adjustment: t('billing.reasons.adjustment', 'Adjustment'),
    };
    return labels[reason] || reason;
  };

  const getTransactionDetails = (reason: string, metadata: any): string => {
    if (!metadata) return '';

    try {
      // Ideas Generation: show geo, idea count, keywords, and trends availability
      if (reason === 'content_generation' && metadata.sourceType === 'ideas_generation') {
        const parts: string[] = [];

        // Add geo (region)
        if (metadata.geo) {
          parts.push(metadata.geo);
        }

        // Add idea count
        if (metadata.ideasCount) {
          parts.push(`${metadata.ideasCount} idea${metadata.ideasCount > 1 ? 's' : ''}`);
        }

        // Add keywords if present
        if (metadata.keywords) {
          parts.push(metadata.keywords);
        }

        // Add trends indicator
        if (metadata.trendsAvailable) {
          parts.push('✓ Trends');
        }

        return parts.length > 0 ? `(${parts.join(' • ')})` : '';
      }

      // Content Generation: show source type, platform count, and token details
      if (reason === 'content_generation' && metadata.sourceType) {
        const sourceType = metadata.sourceType === 'idea' ? 'Idea' :
                          metadata.sourceType === 'blog' ? 'Blog' :
                          metadata.sourceType === 'video' ? 'Video' : metadata.sourceType;
        const platforms = metadata.platformCount ? `${metadata.platformCount} platform${metadata.platformCount > 1 ? 's' : ''}` : '';

        // Calculate total tokens from costBreakdown if available
        let tokenInfo = '';
        if (metadata.costBreakdown) {
          const promptTokens = metadata.costBreakdown.prompt?.tokens || 0;
          const generationTokens = metadata.costBreakdown.generation?.tokens || 0;
          const totalTokens = promptTokens + generationTokens;
          tokenInfo = ` • ${totalTokens} tokens`;
        }

        return `(${sourceType}${platforms ? ` → ${platforms}` : ''}${tokenInfo})`;
      }

      // Brand Extraction: show URL and token breakdown if available
      if (reason === 'brand_extraction' && metadata.url) {
        const url = new URL(metadata.url);
        const tokens = metadata.totalTokens || metadata.visionTokens || '';
        return `(${url.hostname}${tokens ? ` • ${tokens} tokens` : ''})`;
      }

      // Taxonomy Keywords: show keyword count
      if (reason === 'taxonomy_keywords' && metadata.keywordCount) {
        return `(${metadata.keywordCount} keywords)`;
      }

      // Taxonomy Embedding: show node name or title
      if (reason === 'taxonomy_embedding') {
        if (metadata.nodeName) {
          return `(${metadata.nodeName})`;
        }
        if (metadata.nodeTitle) {
          return `(${metadata.nodeTitle})`;
        }
        if (metadata.nodeCount) {
          return `(${metadata.nodeCount} nodes)`;
        }
      }

      // Content Scraping: show competitor name or domain
      if (reason === 'content_scraping') {
        if (metadata.competitorName) {
          return `(${metadata.competitorName})`;
        }
        if (metadata.urlCount) {
          return `(${metadata.urlCount} URLs)`;
        }
        if (metadata.url) {
          try {
            const url = new URL(metadata.url);
            return `(${url.hostname})`;
          } catch {
            return '';
          }
        }
      }

      // Blog Optimization: show text length or segments
      if (reason === 'blog_optimization') {
        if (metadata.segments) {
          return `(${metadata.segments} segments)`;
        }
        if (metadata.text_length) {
          return `(${Math.round(metadata.text_length / 1000)}k chars)`;
        }
      }

      // Video Processing: show duration or clip count
      if (reason === 'video_processing') {
        if (metadata.clipCount) {
          return `(${metadata.clipCount} clips)`;
        }
        if (metadata.duration) {
          return `(${Math.round(metadata.duration)}s)`;
        }
      }

      // Taxonomy Generation: show node count
      if (reason === 'taxonomy_generation' && metadata.nodeCount) {
        return `(${metadata.nodeCount} nodes)`;
      }

      return '';
    } catch (error) {
      console.error('Error parsing transaction metadata:', error);
      return '';
    }
  };

  const toggleTransactionExpanded = (transactionId: string) => {
    setExpandedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  };

  const toggleJobGroupCollapsed = (jobId: string) => {
    setCollapsedJobGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  // Group transactions by jobId (only groups with 2+ items)
  const groupTransactionsByJob = (transactions: LedgerEntry[]) => {
    const grouped: { [key: string]: LedgerEntry[] } = {};
    const ungrouped: LedgerEntry[] = [];

    transactions.forEach(entry => {
      const jobId = entry.metadata?.jobId;
      if (jobId && typeof jobId === 'string') {
        if (!grouped[jobId]) {
          grouped[jobId] = [];
        }
        grouped[jobId].push(entry);
      } else {
        ungrouped.push(entry);
      }
    });

    // Move single-item groups to ungrouped
    Object.entries(grouped).forEach(([jobId, entries]) => {
      if (entries.length === 1) {
        ungrouped.push(entries[0]);
        delete grouped[jobId];
      }
    });

    return { grouped, ungrouped };
  };

  const renderExpandedMetadata = (entry: LedgerEntry) => {
    if (!entry.metadata) {
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No metadata available
        </div>
      );
    }

    const metadata = entry.metadata;
    const isDev = import.meta.env.DEV;

    return (
      <div className="space-y-3 text-sm">
        {/* Cost Breakdown */}
        {metadata.costBreakdown && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Token Usage:</h4>
            <div className="space-y-1">
              {metadata.costBreakdown.prompt && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Prompt ({metadata.costBreakdown.prompt.description || 'Input tokens'}):
                  </span>
                  <span className="font-mono font-bold dark:text-white">
                    {metadata.costBreakdown.prompt.tokens.toLocaleString()} tokens
                    {isDev && metadata.costBreakdown.prompt.usd && (
                      <span className="ml-2 text-gray-500 dark:text-gray-400">
                        (${metadata.costBreakdown.prompt.usd.toFixed(5)})
                      </span>
                    )}
                  </span>
                </div>
              )}
              {metadata.costBreakdown.generation && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Generation ({metadata.costBreakdown.generation.description || 'Output tokens'}):
                  </span>
                  <span className="font-mono font-bold dark:text-white">
                    {metadata.costBreakdown.generation.tokens.toLocaleString()} tokens
                    {isDev && metadata.costBreakdown.generation.usd && (
                      <span className="ml-2 text-gray-500 dark:text-gray-400">
                        (${metadata.costBreakdown.generation.usd.toFixed(5)})
                      </span>
                    )}
                  </span>
                </div>
              )}
              {isDev && metadata.usdCost && (
                <div className="flex justify-between pt-1 mt-1 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400 font-bold">Total Cost:</span>
                  <span className="font-mono font-bold text-green-600 dark:text-green-400">
                    ${metadata.usdCost.toFixed(5)} USD
                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                      (€{(metadata.usdCost * 0.92).toFixed(5)})
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ideas Generation Details */}
        {entry.reason === 'content_generation' && metadata.sourceType === 'ideas_generation' && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Ideas Generation Details:</h4>
            <div className="space-y-1">
              {metadata.geo && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Region:</span>
                  <span className="font-bold dark:text-white">{metadata.geo}</span>
                </div>
              )}
              {metadata.ideasCount && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Ideas Generated:</span>
                  <span className="font-bold dark:text-white">{metadata.ideasCount}</span>
                </div>
              )}
              {metadata.keywords && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Keywords:</span>
                  <span className="font-bold dark:text-white">{metadata.keywords}</span>
                </div>
              )}
              {metadata.trendsAvailable !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Google Trends:</span>
                  <span className="font-bold dark:text-white">
                    {metadata.trendsAvailable ? '✓ Available' : '✗ Unavailable'}
                  </span>
                </div>
              )}
              {metadata.trendingTopicsCount !== undefined && metadata.trendingTopicsCount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Trending Topics Used:</span>
                  <span className="font-bold dark:text-white">{metadata.trendingTopicsCount}</span>
                </div>
              )}
              {metadata.promptTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Prompt Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.promptTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.completionTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Completion Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.completionTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.totalTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.totalTokens.toLocaleString()} tokens</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Generation Details */}
        {entry.reason === 'content_generation' && metadata.sourceType !== 'ideas_generation' && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Content Details:</h4>
            <div className="space-y-1">
              {metadata.sourceType && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Source Type:</span>
                  <span className="font-bold dark:text-white capitalize">{metadata.sourceType}</span>
                </div>
              )}
              {metadata.platformCount && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Platforms:</span>
                  <span className="font-bold dark:text-white">{metadata.platformCount}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Brand Extraction Details */}
        {entry.reason === 'brand_extraction' && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Brand Extraction Details:</h4>
            <div className="space-y-1">
              {metadata.visionTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Vision API Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.visionTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.promptTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Prompt Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.promptTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.totalTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.totalTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.imageSize && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Screenshot Size:</span>
                  <span className="font-bold dark:text-white">{metadata.imageSize}</span>
                </div>
              )}
              {metadata.tiles && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Image Tiles:</span>
                  <span className="font-bold dark:text-white">{metadata.tiles}</span>
                </div>
              )}
              {metadata.scrollCount !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Scroll Count:</span>
                  <span className="font-bold dark:text-white">{metadata.scrollCount}</span>
                </div>
              )}
              {metadata.fileSize && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">File Size:</span>
                  <span className="font-bold dark:text-white">{(metadata.fileSize / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Taxonomy Generation Details */}
        {entry.reason === 'taxonomy_generation' && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Taxonomy Generation Details:</h4>
            <div className="space-y-1">
              {metadata.promptTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Prompt Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.promptTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.completionTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Completion Tokens:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.completionTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.totalTokens && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total:</span>
                  <span className="font-mono font-bold dark:text-white">{metadata.totalTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {metadata.brandName && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Brand:</span>
                  <span className="font-bold dark:text-white">{metadata.brandName}</span>
                </div>
              )}
              {metadata.categories !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Categories:</span>
                  <span className="font-bold dark:text-white">{metadata.categories}</span>
                </div>
              )}
              {metadata.websiteUrl && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">URL:</span>
                  <span className="font-bold dark:text-white text-xs truncate max-w-xs">
                    <a href={metadata.websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {metadata.websiteUrl}
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Taxonomy Embedding Details */}
        {entry.reason === 'taxonomy_embedding' && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Embedding Details:</h4>
            <div className="space-y-1">
              {metadata.nodeName && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Taxonomy Node:</span>
                  <span className="font-bold dark:text-white">{metadata.nodeName}</span>
                </div>
              )}
              {metadata.operation && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Operation:</span>
                  <span className="font-bold dark:text-white capitalize">{metadata.operation.replace('_', ' ')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Scraping / Auto Discover Details */}
        {entry.reason === 'content_scraping' && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Content Auto Discover:</h4>
            <div className="space-y-1">
              {metadata.competitorName && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Competitor:</span>
                  <span className="font-bold dark:text-white">{metadata.competitorName}</span>
                </div>
              )}
              {metadata.operation && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Operation:</span>
                  <span className="font-bold dark:text-white capitalize">{metadata.operation.replace('_', ' ')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Other metadata fields */}
        {metadata.url && (
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">URL:</h4>
            <a
              href={metadata.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline break-all"
            >
              {metadata.url}
            </a>
          </div>
        )}

        {metadata.keywordCount && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Keywords:</span>
            <span className="font-bold dark:text-white">{metadata.keywordCount}</span>
          </div>
        )}

        {metadata.nodeCount && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Nodes:</span>
            <span className="font-bold dark:text-white">{metadata.nodeCount}</span>
          </div>
        )}

        {metadata.nodeTitle && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Node:</span>
            <span className="font-bold dark:text-white">{metadata.nodeTitle}</span>
          </div>
        )}

        {metadata.segments && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Segments:</span>
            <span className="font-bold dark:text-white">{metadata.segments}</span>
          </div>
        )}

        {metadata.text_length && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Text Length:</span>
            <span className="font-bold dark:text-white">
              {metadata.text_length.toLocaleString()} characters
            </span>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold dark:text-white">
            {t('billing.title', 'Billing & Usage')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('billing.description', 'Manage your subscription, tokens, and invoices')}
          </p>
        </div>

        {/* Loading skeletons */}
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-48 bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-700 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold dark:text-white">
          {t('billing.title', 'Billing & Usage')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('billing.description', 'Manage your subscription, tokens, and invoices')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
          <p className="text-sm text-green-900 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
          <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Subscription Management Card */}
      {subscription && (
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold dark:text-white">
              {t('billing.subscription.title', 'Subscription')}
            </h2>
            {subscription.plan === 'free' ? (
              <Button
                onClick={() => setShowUpgradeModal(true)}
                variant="primary"
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                {t('billing.subscription.upgrade', 'Upgrade Plan')}
              </Button>
            ) : (
              <Button
                onClick={handleManageSubscription}
                variant="primary"
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('billing.subscription.manage', 'Manage Subscription')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plan & Status */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('billing.subscription.plan', 'Plan')}
                </label>
                <span
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold border-2 ${getPlanColor(
                    subscription.plan
                  )}`}
                >
                  {getPlanLabel(subscription.plan)}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('billing.subscription.status', 'Status')}
                </label>
                <span className={`text-sm font-semibold ${getStatusColor(subscription.status)}`}>
                  {getStatusLabel(subscription.status)}
                </span>
              </div>
            </div>

            {/* Tokens & Price */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('billing.subscription.monthlyTokens', 'Monthly Tokens')}
                </label>
                <p className="text-2xl font-bold dark:text-white">
                  {(subscription.monthlyTokens ?? 0).toLocaleString()}
                  {subscription.plan === 'free' && (
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                      ({t('billing.subscription.tokenPacksOnly', 'Purchase token packs')})
                    </span>
                  )}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('billing.subscription.price', 'Price')}
                </label>
                <p className="text-2xl font-bold dark:text-white">
                  {formatCurrency(subscription.priceMonthly ?? 0)}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/month</span>
                </p>
              </div>
            </div>

            {/* Renewal/Cancellation Info */}
            <div className="md:col-span-2">
              {subscription.cancelAtPeriodEnd ? (
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500 rounded-lg">
                  <p className="text-sm text-orange-900 dark:text-orange-200">
                    <strong>{t('billing.subscription.cancelScheduled', 'Subscription Canceled')}</strong>
                    <br />
                    {t(
                      'billing.subscription.endsOn',
                      'Your subscription will end on {{date}}',
                      {
                        date: subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'N/A',
                      }
                    )}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('billing.subscription.renewalDate', 'Next Renewal')}
                  </label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'N/A'}
                  </p>
                </div>
              )}

              {/* Customer ID (for support) */}
              <div className="mt-4 pt-4 border-t-2 border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('billing.subscription.customerId', 'Customer ID')}: {subscription.stripeCustomerId}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Token Wallet Card */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold dark:text-white">
            {t('billing.wallet.title', 'Token Wallet')}
          </h2>
          {/* Debug button - only show in development */}
          {import.meta.env.DEV && (
            <Button
              variant="secondary"
              onClick={() => setShowDebugModal(true)}
              className="text-xs"
            >
              🔧 Add Test Tokens
            </Button>
          )}
        </div>

        {/* Balance Display */}
        <div className="text-center py-8">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {t('billing.wallet.balance', 'Current Balance')}
          </p>
          <p className="text-5xl font-bold dark:text-white">
            {liveBalance.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('billing.wallet.tokens', 'tokens')}
          </p>
        </div>
      </Card>

      {/* Token Packs Purchase Card */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">
          {t('billing.tokenPacks.title', 'Buy Token Packs')}
        </h2>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('billing.tokenPacks.description', 'Purchase additional tokens to use for content generation, video processing, and more')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TOKEN_PACKS.map((pack) => (
            <div
              key={pack.size}
              className="p-6 border-2 border-gray-300 dark:border-gray-700 rounded-lg hover:border-black dark:hover:border-white transition-colors"
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold dark:text-white mb-1">
                  {t(`billing.tokenPacks.${pack.size}`, pack.size.charAt(0).toUpperCase() + pack.size.slice(1))}
                </h3>
                <p className="text-3xl font-bold dark:text-white">{pack.label}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pack.description}</p>
              </div>

              <Button
                variant="primary"
                onClick={() => handlePurchaseTokens(pack.size)}
                className="w-full"
              >
                {t('billing.tokenPacks.buyNow', 'Buy Now')}
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Transaction History Card */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold dark:text-white">
            {t('billing.transactions.title', 'Transaction History')}
          </h2>

          <Button variant="secondary" onClick={handleExportCSV}>
            {t('billing.transactions.exportCsv', 'Export to CSV')}
          </Button>
        </div>

        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              {t('billing.transactions.empty', 'No transactions yet')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                    <th className="w-8 py-3 px-2"></th>
                    <th className="text-left py-3 px-4 text-sm font-bold dark:text-white">
                      {t('billing.transactions.columns.date', 'Date')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-bold dark:text-white">
                      {t('billing.transactions.columns.reason', 'Reason')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-bold dark:text-white">
                      {t('billing.transactions.columns.amount', 'Amount')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-bold dark:text-white">
                      {t('billing.transactions.columns.balance', 'Balance After')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const { grouped, ungrouped } = groupTransactionsByJob(transactions);

                    // Create an array of items with their sort dates for proper ordering
                    const sortableItems: Array<{ date: string; type: 'group' | 'single'; data: any }> = [];

                    // Add grouped items (use first entry's date)
                    Object.entries(grouped).forEach(([jobId, entries]) => {
                      sortableItems.push({
                        date: entries[0].createdAt,
                        type: 'group',
                        data: { jobId, entries }
                      });
                    });

                    // Add ungrouped items
                    ungrouped.forEach(entry => {
                      sortableItems.push({
                        date: entry.createdAt,
                        type: 'single',
                        data: entry
                      });
                    });

                    // Sort all items by date DESC (newest first)
                    sortableItems.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                    // Helper function to render a single transaction row
                    const renderTransactionRow = (entry: LedgerEntry, isChild: boolean = false) => {
                      const isExpanded = expandedTransactions.has(entry.id);
                      const hasMetadata = entry.metadata && Object.keys(entry.metadata).length > 0;

                      return (
                        <React.Fragment key={entry.id}>
                          <tr className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${isChild ? 'bg-gray-50/50 dark:bg-gray-800/20' : ''}`}>
                            <td className="py-3 px-2">
                              {hasMetadata && (
                                <button
                                  onClick={() => toggleTransactionExpanded(entry.id)}
                                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-transform"
                                  style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              )}
                            </td>
                            <td className={`py-3 px-4 text-sm dark:text-gray-300 ${isChild ? 'pl-8' : ''}`}>
                              {formatDate(entry.createdAt)}
                            </td>
                            <td className={`py-3 px-4 text-sm dark:text-gray-300 ${isChild ? 'pl-8' : ''}`}>
                              <div className="flex flex-col gap-0.5">
                                <span className="font-medium">{getTransactionReasonLabel(entry.reason)}</span>
                                {getTransactionDetails(entry.reason, entry.metadata) && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {getTransactionDetails(entry.reason, entry.metadata)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`py-3 px-4 text-sm text-right ${
                              entry.deltaTokens > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="font-bold">{formatTokens(entry.deltaTokens)}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatUsdCost(entry.deltaTokens)}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-right dark:text-gray-300">
                              {entry.balanceAfter.toLocaleString()} tokens
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30">
                              <td></td>
                              <td colSpan={4} className="py-4 px-4">
                                <div className="border-l-2 border-gray-300 dark:border-gray-600 pl-4">
                                  {renderExpandedMetadata(entry)}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    };

                    // Render items in sorted order
                    return sortableItems.flatMap(item => {
                      if (item.type === 'group') {
                        const { jobId, entries } = item.data;
                        const isCollapsed = collapsedJobGroups.has(jobId);
                        const totalTokens = entries.reduce((sum: number, e: LedgerEntry) => sum + e.deltaTokens, 0);
                        const firstEntry = entries[0];

                        // Get a human-readable job name from the first entry
                        const jobName = firstEntry.metadata?.brandName ||
                                        firstEntry.metadata?.websiteUrl ||
                                        getTransactionReasonLabel(firstEntry.reason);

                        const rows = [
                          // Parent row with job summary
                          <tr key={`job-${jobId}`} className="border-b-2 border-gray-300 dark:border-gray-600 bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100/50 dark:hover:bg-blue-900/20">
                            <td className="py-3 px-2">
                              <button
                                onClick={() => toggleJobGroupCollapsed(jobId)}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-transform"
                                style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </td>
                            <td className="py-3 px-4 text-sm dark:text-gray-300">
                              {formatDate(firstEntry.createdAt)}
                            </td>
                            <td className="py-3 px-4 text-sm dark:text-gray-300">
                              <div className="flex flex-col gap-0.5">
                                <span className="font-bold text-blue-700 dark:text-blue-300">{jobName}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {entries.length} operation{entries.length > 1 ? 's' : ''} • Job {jobId.slice(0, 8)}...
                                </span>
                              </div>
                            </td>
                            <td className={`py-3 px-4 text-sm text-right ${
                              totalTokens > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="font-bold">{formatTokens(totalTokens)}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatUsdCost(totalTokens)}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-right dark:text-gray-300">
                              {firstEntry.balanceAfter.toLocaleString()} tokens
                            </td>
                          </tr>
                        ];

                        // Add child rows if not collapsed
                        if (!isCollapsed) {
                          entries.forEach((entry: LedgerEntry) => {
                            rows.push(renderTransactionRow(entry, true));
                          });
                        }

                        return rows;
                      } else {
                        // Single ungrouped transaction
                        return renderTransactionRow(item.data, false);
                      }
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {hasMoreTransactions && (
              <div className="mt-4 text-center">
                <Button variant="secondary" onClick={loadMoreTransactions}>
                  {t('billing.transactions.loadMore', 'Load More')}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Invoices Card */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">
          {t('billing.invoices.title', 'Invoices')}
        </h2>

        {invoices.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              {t('billing.invoices.empty', 'No invoices yet')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-bold dark:text-white">
                    {t('billing.invoices.columns.invoiceNumber', 'Invoice #')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-bold dark:text-white">
                    {t('billing.invoices.columns.date', 'Date')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-bold dark:text-white">
                    {t('billing.invoices.columns.amount', 'Amount')}
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-bold dark:text-white">
                    {t('billing.invoices.columns.status', 'Status')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-bold dark:text-white">
                    {t('billing.invoices.columns.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-3 px-4 text-sm font-mono dark:text-gray-300">
                      {invoice.invoiceNumber || `INV-${invoice.id.substring(0, 8)}`}
                    </td>
                    <td className="py-3 px-4 text-sm dark:text-gray-300">
                      {formatDate(invoice.createdAt)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-bold dark:text-gray-300">
                      {formatCurrency(invoice.amountPaid)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${
                        invoice.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                        invoice.status === 'open' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                        'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-right">
                      <div className="flex gap-2 justify-end">
                        <a
                          href={invoice.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {t('billing.invoices.actions.view', 'View')}
                        </a>
                        {invoice.invoicePdf && (
                          <>
                            <span className="text-gray-300 dark:text-gray-600">|</span>
                            <a
                              href={invoice.invoicePdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {t('billing.invoices.actions.download', 'Download PDF')}
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Debug Modal - Add Test Tokens */}
      {showDebugModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 dark:text-white">
              🔧 Add Test Tokens
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This is a development-only feature to add tokens for testing purposes.
            </p>

            <Input
              label="Number of Tokens"
              type="number"
              value={debugTokenAmount}
              onChange={(e) => setDebugTokenAmount(e.target.value)}
              placeholder="10000"
              min="1"
            />

            <div className="flex gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDebugModal(false);
                  setDebugTokenAmount('10000');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDebugAddTokens}
                className="flex-1"
              >
                Add Tokens
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentPlan={subscription?.plan || 'free'}
      />
    </div>
  );
}
